// StartJobModal.jsx
import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { insertTimestampIntoText } from "../../../helpers/getTimestamp";
import ButtonAddTimestamp from "../../../utils/ButtonAddTimestamp";
import AssignedByListBox from "./AssignedByListBox";
import ButtonModal from "./ButtonModal";
import ErrorMessage from "./ErrorMessage";
import InputDateModal from "./InputDateModal";
import InputTextAreaModal from "./InputTextAreaModal";
import { updateTaskService } from "../../../services/clientServiceSubscriptionTask/updateTaskService";
import { getSubscriptionTeamMembers } from "../../../services/clientServiceSubscription/getSubscriptionTeamMembers";
import { usePermissions } from "../../../context/PermissionsContext";

const StartJobModal = ({
    open,
    setOpen,
    nextJob,
    setNextjob,
    loggedInEmployee,
    initializeJobs,
    getNewJobs,
    clientServiceSubscriptionId,
}) => {
    const { can, permissionsLoading } = usePermissions();
    const canWriteSteps = can("steps.write");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [displayNotes, setDisplayNotes] = useState(false);
    const [errors, setErrors] = useState([]);
    const completeButtonRef = useRef(null);
    const notesRef = useRef(null);
    const [teamMembers, setTeamMembers] = useState([]);

    useEffect(() => {
        if (permissionsLoading || !canWriteSteps || !clientServiceSubscriptionId)
            return;
        const getTeamMembers = async () => {
            const response = await getSubscriptionTeamMembers(
                clientServiceSubscriptionId,
            );
            setTeamMembers(response.data.teamMembers);
        };
        getTeamMembers();
    }, [open, clientServiceSubscriptionId, canWriteSteps, permissionsLoading]);

    useEffect(() => {
        console.log("nextJob", nextJob);
    }, []);

    const toggleNotes = () => {
        setDisplayNotes(!displayNotes);
    };

    const onHandleCancel = () => {
        setErrors([]);
        initializeJobs();
        setOpen(false);
    };

    const onHandleChangeAssignedEmployees = (newAssignedEmployees) => {
        setNextjob({
            ...nextJob,
            assignedEmployees: [newAssignedEmployees],
            assignedDate: moment().format("YYYY-MM-DD"),
        });
    };

    const onHandleChange = (e) => {
        const { name, value } = e.target;
        setNextjob({ ...nextJob, [name]: value });
    };

    const onHandleClickAddTimestamp = (fieldName) => {
        const textarea = notesRef.current;
        if (!textarea) return;

        const { selectionStart, selectionEnd } = textarea;
        const current = nextJob[fieldName] || "";
        const { caretPosition, value: updated } = insertTimestampIntoText(
            current,
            selectionStart,
            selectionEnd,
            loggedInEmployee,
        );

        setNextjob({ ...nextJob, [fieldName]: updated });

        textarea.focus();
        textarea.setSelectionRange(caretPosition, caretPosition);
    };

    const validateForm = (form) => {
        if (form.assignedEmployees.length < 1) {
            setErrors({ assignedEmployees: "This field cannot be empty." });
            return false;
        }
        if (form.dueDate === "0001-01-01T00:00:00") {
            setErrors({ dueDate: "You need to choose a due date." });
            return false;
        }
        return true;
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (permissionsLoading || !canWriteSteps || isSubmitting) return;
        if (!validateForm(nextJob)) {
            return;
        }
        setIsSubmitting(true);
        const jobInProgress = {
            ...nextJob,
            status: 1,
            assignedByEmployee: loggedInEmployee,
        };
        try {
            const updateNextJobResponse = await updateTaskService(
                nextJob.id,
                jobInProgress,
            );
            if (updateNextJobResponse.data.success) {
                getNewJobs();
                setOpen(false);
                setErrors([]);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateAssignedEmployee = (assignedEmployees) => {
        if (Array.isArray(assignedEmployees)) {
            return assignedEmployees.length > 0 ? assignedEmployees[0] : null;
        }
        return assignedEmployees || null;
    };

    const validateDueDate = (dueDate) => {
        if (dueDate === "0001-01-01T00:00:00") {
            setNextjob({
                ...nextJob,
                dueDate: new Date().toISOString().split("T")[0],
            });
        }
        return dueDate;
    };

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={() => setOpen(false)}
                    className="relative z-50"
                >
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                    <div className="fixed inset-0 w-screen overflow-y-auto">
                        <div className="flex items-center justify-center min-h-full p-4">
                            <Dialog.Panel className="relative w-full px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:max-w-lg sm:p-6">
                                <Dialog.Title className="w-full text-lg font-medium leading-6 text-center text-gray-900">
                                    Assign step {nextJob.name}
                                </Dialog.Title>

                                <form onSubmit={onHandleSubmit}>
                                    <AssignedByListBox
                                        label="Employee"
                                        employees={teamMembers}
                                        assignedBy={validateAssignedEmployee(
                                            nextJob.assignedEmployees,
                                        )}
                                        onChange={onHandleChangeAssignedEmployees}
                                    />
                                    <ErrorMessage
                                        error={errors["assignedEmployees"]}
                                    />

                                    {nextJob.assignedEmployees.length > 0 && (
                                        <>
                                            <InputDateModal
                                                label="Due Date"
                                                name="dueDate"
                                                onChange={onHandleChange}
                                                value={validateDueDate(
                                                    nextJob.dueDate,
                                                )}
                                            />
                                            <ErrorMessage
                                                error={errors["dueDate"]}
                                            />
                                        </>
                                    )}

                                    {!displayNotes ? (
                                        <button
                                            className="inline-flex items-center p-1 mt-4 text-base text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                            onClick={toggleNotes}
                                            type="button"
                                        >
                                            Add notes
                                        </button>
                                    ) : (
                                        <button
                                            className="inline-flex items-center p-1 mt-4 text-base text-white bg-red-500 border border-transparent rounded-sm shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                            onClick={toggleNotes}
                                            type="button"
                                        >
                                            Hide notes
                                        </button>
                                    )}

                                    {displayNotes && (
                                        <>
                                            <InputTextAreaModal
                                                ref={notesRef}
                                                label="Notes"
                                                name="notes"
                                                onChange={onHandleChange}
                                                value={nextJob.notes}
                                            />
                                            <ButtonAddTimestamp
                                                onHandleClick={() =>
                                                    onHandleClickAddTimestamp(
                                                        "notes",
                                                    )
                                                }
                                            />
                                        </>
                                    )}

                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                        <ButtonModal
                                            name="Save"
                                            type="submit"
                                            className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-500 border border-transparent rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 sm:col-start-2 sm:text-sm"
                                            ref={completeButtonRef}
                                            disabled={
                                                permissionsLoading ||
                                                !canWriteSteps ||
                                                isSubmitting
                                            }
                                        />
                                        <ButtonModal
                                            name="Cancel"
                                            type="button"
                                            onClick={onHandleCancel}
                                            className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        />
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default StartJobModal;
